import Route from '@ember/routing/route';
import { hash } from 'rsvp';
import { inject as service } from '@ember/service';
import { ASSIGNMENT } from 'sh-www/models/assignment';
import { CLASSIFICATION_CATEGORIES } from 'sh-www/models/classification';
import { EMBER_ROUTE_PARTIALS } from 'sh-www/constants/routes';
import { POSTING_CREATE__QUERY_PARAMS } from 'sh-www/constants/routes/posting-create';
import { trackPageViewFromTransition } from 'sh-www/utils/datalayer';
import BuyerOnlyRouteMixin from 'sh-www/mixins/buyer-only-route-mixin';

class PostingCreateRoute extends Route.extend(BuyerOnlyRouteMixin) {
  @service('states/routes/posting-create') state;
  @service('state-managers/routes/posting-create') stateManager;

  @service api;
  @service errorTracker;
  @service session;
  @service store;

  async model() {
    const params = this.paramsFor(EMBER_ROUTE_PARTIALS.POSTING__CREATE);
    const cloneId = params[POSTING_CREATE__QUERY_PARAMS.CLONE_ID] ?? '';
    const companyId = this.session.currentUser.company.id;

    this.stateManager.setPostingType(ASSIGNMENT.TYPES.STANDARD);
    this.stateManager.setCreatedFrom(ASSIGNMENT.CREATED_FROM.STANDARD);
    this.stateManager.setSelect(false);

    const services = await this.stateManager.loadServices();
    const classifications = await this.stateManager.loadClassifications();

    // pre-fill services from param
    const serviceIdsParam = params[POSTING_CREATE__QUERY_PARAMS.SERVICE_IDS];
    if (serviceIdsParam) {
      const serviceIds = serviceIdsParam.split(',');
      const prefillServices = services.filter(({ id }) => {
        return serviceIds.includes(id);
      });

      this.stateManager._updateServiceStateFromServices(prefillServices);
    }

    // pre-fill classifications from param
    const classificationIdsParam = params[POSTING_CREATE__QUERY_PARAMS.CLASSIFICATION_IDS];
    if (classificationIdsParam) {
      const classificationIds = classificationIdsParam.split(',');
      const prefillClassifications = classifications.filter(({ id }) => {
        return classificationIds.includes(id);
      });

      prefillClassifications.forEach((classification) => {
        const classificationOption = {
          key: classification.label,
          model: classification.id,
        };

        if (classification.category === CLASSIFICATION_CATEGORIES.CATEGORY_SUBJECT) {
          this.stateManager.addClassificationSubject(classificationOption);
        } else if (classification.category === CLASSIFICATION_CATEGORIES.CATEGORY_INDUSTRY) {
          this.stateManager.addClassificationIndustry(classificationOption);
        }
      });
    }

    // pre-fill location from param
    const locationIdParam = params[POSTING_CREATE__QUERY_PARAMS.LOCATION_ID];
    if (locationIdParam) {
      try {
        const location = await this.store.findRecord('location', locationIdParam);
        this.stateManager.updateLocation(location);
      } catch(e) {
        this.errorTracker.error(`Failed to find prefilled location id ${locationIdParam}`, this, e);
      }
    }

    // pre-fill max rate from param
    const maxRateParam = params[POSTING_CREATE__QUERY_PARAMS.MAX_RATE];
    if (maxRateParam) {
      this.stateManager.updatedBudgetHighValue(maxRateParam);
    }

    // pre-fill rate type from param
    const rateTypeParam = params[POSTING_CREATE__QUERY_PARAMS.RATE_TYPE];
    if (rateTypeParam) {
      this.stateManager.updateRateType(Number.parseInt(rateTypeParam));
    }

    return hash({
      classifications,
      clonedAssignment: this.stateManager.loadClonedAssignment(cloneId),
      companyMemberships: this.stateManager.loadCompanyWithMembers(companyId),
      companyTeams: this.stateManager.loadCompanyTeams(companyId),
      countries: this.stateManager.loadCountries(),
      regions: this.stateManager.loadRegions(),
      services,
      productions: this.getProductions(companyId),
    });
  }

  activate(transition) {
    super.activate(...arguments);
    trackPageViewFromTransition(this, transition, '/posting/create/');
  }

  async getProductions(company_id) {
    const params = {
      filter: { company_id },
      count: 50,
    };

    const response = await this.api.get('productions', { params });
    this.store.pushPayload(response);

    return response.data.map(({ id }) =>
      this.store.peekRecord('production', id),
    );
  }
}

export default PostingCreateRoute;
